import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { of, Subscription, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import {
  IQuerySubmission,
  isDisplayableResultStatus,
  isFailedQueryStatus,
  isPartialQueryStatus,
  isTerminalQueryStatus,
} from '../../models/IQuerySubmission';
import { SqlQueryService } from '../../services/sql-query.service';

@Component({
  selector: 'app-query-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    CommonModule,
  ],
  templateUrl: './query-form.component.html',
  styleUrl: './query-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueryFormComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private queryService = inject(SqlQueryService);
  private snackBar = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);
  private auth = inject(AuthService);
  private router = inject(Router);

  readonly pollingIntervalMs = 1000;
  readonly maxPollingMs = 30000 * 1000000;
  readonly exampleQueries: { sql: string; buttonLabel: string }[] = [
    {
      sql: 'SELECT * FROM mpc_orbits LIMIT 10;',
      buttonLabel: 'Recent MPC orbits',
    },
    {
      sql: 'SELECT unpacked_primary_provisional_designation, epoch_mjd, q FROM mpc_orbits ORDER BY epoch_mjd DESC LIMIT 25;',
      buttonLabel: 'Latest perihelion epochs',
    },
    {
      sql: 'SELECT * FROM obs_sbn WHERE mag < 20 LIMIT 50;',
      buttonLabel: 'Bright observations',
    },
    {
      sql: 'SELECT * FROM obs_sbn WHERE mag > 20 LIMIT 50;',
      buttonLabel: 'Dim observations',
    },
    //
    {
      sql: 'SELECT * FROM neocp_els where digest2 > 99;',
      buttonLabel: 'Find the highest digest2 scoring NEOCP entries',
    },
    {
      sql: "SELECT * FROM neocp_prev_des where iau_desig like '%2026 A%'",
      buttonLabel:
        'Find objects that were on the neocp previously, with a designation in a particular half-month',
    },
  ];

  queryForm = this.fb.nonNullable.group({
    query: [environment.DEFAULT_QUERY, [Validators.required, Validators.minLength(10)]],
  });

  isSubmitting = false;
  errorMessage: string | null = null;
  querySubmission: IQuerySubmission | null = null;
  hasSubmitted = false;

  private pollSub: Subscription | null = null;

  submitQuery(): void {
    if (this.queryForm.invalid) return;
    this.resetState();
    this.isSubmitting = true;
    this.hasSubmitted = true;
    const sql = this.queryForm.controls.query.value.trim();
    this.querySubmission = this.buildPendingSubmission(sql);

    this.queryService.submitQuery(sql).subscribe({
      next: (submission) => {
        this.querySubmission = submission;
        this.isSubmitting = false;
        this.startPolling(submission);
        this.mark();
      },
      error: (err) => {
        const message = this.describeError(err);
        this.isSubmitting = false;
        this.errorMessage = message;
        this.openSnackBar(message, true);
        // If token expired/unauthorized, force re-login UX
        const code = err?.status;
        const errMsg = err?.error?.error || err?.error?.message || '';
        if (code === 401 || code === 403 || /token has expired/i.test(errMsg)) {
          this.querySubmission = null;
          this.hasSubmitted = false;
          this.openSnackBar('Session expired. Please sign in again.', true);
        } else {
          this.hasSubmitted = false;
          this.querySubmission = null;
        }
        this.mark();
      },
    });
  }

  private startPolling(submission: IQuerySubmission) {
    this.pollSub?.unsubscribe();
    const start = Date.now();

    this.pollSub = timer(1000, this.pollingIntervalMs)
      .pipe(
        switchMap(() =>
          this.queryService.fetchSubmissionMeta(this.querySubmission ?? submission).pipe(
            catchError((err) => {
              const code = err?.status || err?.name;
              const withinWindow = Date.now() - start <= this.maxPollingMs;
              if (withinWindow && (code === 404 || code === 'NoSuchKey' || code === 400)) {
                const pendingSnapshot = this.querySubmission ?? submission;
                return of({ ...pendingSnapshot });
              }
              throw err;
            }),
          ),
        ),
        takeWhile(() => Date.now() - start <= this.maxPollingMs, true),
      )
      .subscribe({
        next: (meta) => {
          const merged: IQuerySubmission = {
            ...(this.querySubmission ?? submission),
            ...meta,
            status: (meta.status || this.querySubmission?.status || '').trim().toLowerCase() as any,
          };
          this.querySubmission = merged;
          const status = merged.status;
          if (isDisplayableResultStatus(status)) {
            this.stopPolling();
            void this.navigateToResults(merged);
          } else if (isFailedQueryStatus(status)) {
            this.handleErrorState(merged, merged.error_message || 'Query failed.');
          }
          this.mark();
        },
        error: (err) => {
          this.errorMessage = 'Failed to poll query status.';
          this.isSubmitting = false;
          this.stopPolling();
          this.handleErrorState(
            {
              ...(this.querySubmission ?? submission),
              status: 'error',
            } as IQuerySubmission,
            this.describeError(err),
          );
          this.mark();
        },
        complete: () => {
          const status = (this.querySubmission?.status || '').trim().toLowerCase();
          if (!isTerminalQueryStatus(status) && !this.errorMessage) {
            this.handleErrorState(
              {
                ...(this.querySubmission ?? submission),
                status: 'error',
              } as IQuerySubmission,
              'Query status did not update in time. Please retry.'
            );
            this.mark();
          }
        },
      });
  }

  private stopPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  clearResults() {
    this.querySubmission = null;
    this.errorMessage = null;
    this.hasSubmitted = false;
    this.stopPolling();
    this.mark();
  }

  isPendingStatus(status: string | null | undefined): boolean {
    const normalized = (status || '').toLowerCase();
    return normalized === 'pending' || normalized === 'queued' || normalized === 'running' || normalized === 'processing';
  }

  isPartialStatus(status: string | null | undefined): boolean {
    return isPartialQueryStatus(status);
  }

  private handleErrorState(submission: IQuerySubmission, message: string) {
    this.querySubmission = { ...submission, error_message: message };
    this.openSnackBar(message, true);
    this.isSubmitting = false;
    this.stopPolling();
  }

  formatCreationTime(creationTime: string | undefined): string {
    if (!creationTime) return '';
    const parsed = this.parseCreationTime(creationTime);
    if (!parsed) return creationTime;
    return parsed.toLocaleString();
  }

  applyExample(query: string) {
    this.queryForm.patchValue({ query });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private buildPendingSubmission(query: string): IQuerySubmission {
    const userEmail = this.auth.user()?.email || 'user';
    return {
      job_id: '',
      query,
      status: 'pending',
      username: userEmail,
      creation_time: new Date().toISOString(),
    };
  }

  private describeError(err: any): string {
    const message =
      err?.error?.message || err?.message || 'Something went wrong. Please try again.';
    return message;
  }

  private resetState() {
    this.errorMessage = null;
    this.querySubmission = null;
    this.stopPolling();
  }

  private openSnackBar(message: string | null, isError = false) {
    if (!message) return;
    this.snackBar.open(message, 'Dismiss', {
      duration: 6000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: isError ? ['query-snackbar', 'query-snackbar-error'] : ['query-snackbar'],
    });
  }

  private mark() {
    this.cdr.markForCheck();
  }

  private async navigateToResults(submission: IQuerySubmission): Promise<void> {
    const jobId = (submission.job_id || '').trim();
    if (!jobId) {
      this.handleErrorState(
        submission,
        'Query completed but no job ID was returned. Please retry.'
      );
      this.mark();
      return;
    }

    try {
      await this.router.navigate(['/results', jobId]);
    } catch {
      this.handleErrorState(
        submission,
        'Query completed, but opening the results page failed. Please retry.'
      );
      this.mark();
    }
  }

  private parseCreationTime(input: string): Date | null {
    const trimmed = (input || '').trim();
    if (!trimmed) return null;

    const numericValue = Number(trimmed);
    if (Number.isFinite(numericValue)) {
      const milliseconds = numericValue < 1e11 ? numericValue * 1000 : numericValue;
      const dateFromNumeric = new Date(milliseconds);
      if (!Number.isNaN(dateFromNumeric.getTime())) return dateFromNumeric;
    }

    const dateFromString = new Date(trimmed);
    if (!Number.isNaN(dateFromString.getTime())) return dateFromString;
    return null;
  }
}
